import { ampli } from '@/ampli';

import { ClientInputError, ClientTransactionError, ClientWalletError } from '@/lib/errors/client';

import { getFailureReason } from '@/components/TransactionModule/MarketMode/SubmitTrade/utils';
import { trackRwaSwapInitiated } from '@/components/TransactionModule/utils';

import type { Price, Quote, SolanaSwapQuote } from '@/utils/0x.types';
import { trackError } from '@/utils/errors';
import { fillSolanaOrder } from '@/utils/solana';
import { getSubmissionFailure } from '@/utils/submissionReason';
import { trackTransactionFailed } from '@/utils/transactionAnalytics';
import { isUserRejection } from '@/utils/wallet';

import type { FailureEvent } from '../analytics';
import { buildTokenEventFields, matchaReferrer, slippageFromQuote } from '../analytics';
import type { ExecutionContext, PreflightResult, SubmitResult, TradeStrategy } from '../types';

const WALLET_CONNECTION_ERROR_MESSAGE =
  'Wallet connection error. Please try again or reconnect your wallet.';

type PreparedSolanaTrade = PreflightResult & {
  raw: Price | Quote | undefined;
};

function buildFailureEvent(
  ctx: ExecutionContext,
  raw: Price | Quote | undefined,
  error: unknown,
  rejected: boolean,
): FailureEvent {
  const normalizedError = error instanceof Error ? error : new Error(String(error));
  const slippage = raw ? slippageFromQuote(raw) : { bps: undefined, mode: undefined };
  const failure = rejected ? undefined : getSubmissionFailure(normalizedError);
  return {
    sellToken: ctx.sellToken,
    buyToken: ctx.buyToken,
    failureStage: 'submission',
    failureReason: rejected ? 'user_rejected' : getFailureReason(normalizedError),
    submissionReason: failure?.submissionReason,
    revertReason: failure?.revertReason,
    swapOrderType: 'market',
    swapExecutionType: 'standard',
    swapSlippageBps: slippage.bps,
    swapSlippageMode: slippage.mode,
    swapProvider: raw && 'provider' in raw ? raw.provider : undefined,
    matchaReferrer: matchaReferrer(ctx),
    recipientAddress: ctx.recipientAddress,
  };
}

/** Solana market swap strategy using the shared Privy and Jito fill primitive. */
export const solanaStrategy: TradeStrategy<PreparedSolanaTrade> = {
  lane: 'solana',
  preflight: (ctx) =>
    ctx.quote.source === 'swap' ? { chainFamily: 'solana', raw: ctx.quote.raw } : null,
  submit: async (ctx, prepared) => {
    ampli.swapInitiated({
      ...buildTokenEventFields({
        source: 'input',
        sellToken: ctx.sellToken,
        buyToken: ctx.buyToken,
        sellInput: ctx.sellInput,
        buyInput: ctx.buyInput,
      }),
      matcha_referrer: matchaReferrer(ctx),
      recipient_address: ctx.recipientAddress,
      swap_order_type: 'market',
      swap_execution_type: 'standard',
    });

    const fail = async (rejected: boolean, error: Error): Promise<SubmitResult> => {
      await ctx.deps.invalidateTradeQuotes();
      return { success: false, rejected, error };
    };

    if (!ctx.accountAddress) {
      const error = new ClientWalletError(WALLET_CONNECTION_ERROR_MESSAGE);
      trackError(error);
      return fail(false, error);
    }
    if (!ctx.solanaWallet) {
      const error = new ClientWalletError(WALLET_CONNECTION_ERROR_MESSAGE);
      trackError(error);
      return fail(false, error);
    }
    if (!prepared.raw) {
      const error = new ClientInputError('cant submit solana swap order: no quote');
      trackError(error);
      return fail(false, error);
    }

    try {
      if (!('maxSlippageBps' in prepared.raw) || !('transaction' in prepared.raw)) {
        throw new ClientTransactionError('No transaction data provided!');
      }
      const quote: SolanaSwapQuote = prepared.raw;
      const hash = await fillSolanaOrder({
        transaction: quote.transaction,
        walletClient: ctx.solanaWallet,
        signTransaction: ctx.signSolanaTransaction,
      });

      const tokenFields = buildTokenEventFields({
        source: 'quote',
        sellToken: ctx.sellToken,
        buyToken: ctx.buyToken,
        sellAmount: quote.sellAmount,
        buyAmount: quote.buyAmount,
        tokenUsdPrices: ctx.tokenUsdPrices,
      });
      ctx.deps.addTransaction(
        {
          sellToken: ctx.sellToken,
          sellAmount: quote.sellAmount,
          sellTokenUsdPrice: ctx.tokenUsdPrices.sellTokenUsd,
          sellAmountUsd: tokenFields.sell_token_amount_usd,
          buyToken: ctx.buyToken,
          buyAmount: quote.buyAmount,
          buyTokenUsdPrice: ctx.tokenUsdPrices.buyTokenUsd,
          buyAmountUsd: tokenFields.buy_token_amount_usd,
          feeSource: null,
          referrerId: undefined,
          hash,
          timestamp: new Date().toISOString(),
          transactionType: 'solana-swap',
          recipient: ctx.recipientAddress,
          slippageBps: quote.maxSlippageBps,
          slippageMode: quote.appliedSlippageMode,
          provider: quote.provider,
        },
        ctx.accountAddress,
        ctx.chainId,
      );
      ampli.transactionSubmitted({
        ...tokenFields,
        recipient_address: ctx.recipientAddress,
        swap_order_type: 'market',
        swap_execution_type: 'standard',
        swap_slippage_bps: quote.maxSlippageBps,
        swap_slippage_mode: quote.appliedSlippageMode,
        swap_tx_hash: hash,
        swap_provider: quote.provider,
      });
      trackRwaSwapInitiated({
        sellToken: ctx.sellToken,
        buyToken: ctx.buyToken,
        orderType: 'market',
        transactionHash: hash,
      });
      ctx.deps.resetInputs();
      return { success: true, outcome: { kind: 'txHash', value: hash } };
    } catch (error: unknown) {
      const rejected = isUserRejection(error);
      trackTransactionFailed(buildFailureEvent(ctx, prepared.raw, error, rejected));
      if (!rejected) trackError(error);
      return fail(rejected, error instanceof Error ? error : new Error(String(error)));
    }
  },
};
